import { SDLCMetadata } from "@sscs/types";
import { compareValues } from "@sscs/utils";
import { INSIGHTS_ROUTE, INSIGHTS_SEARCH_ROUTE } from "Constants";
import {
  Insight,
  InsightsFilterCategories,
  InsightsFilters,
  MyInsightsSearch,
  QuickInsightsSearch,
  QuickSearchCategory,
} from "./types";

export const FILTER_TO_CATEGORY: Record<keyof InsightsFilters, string> = {
  project_id: "Group",
  package_name: "Group",
  platform: "Version",
  category: "Version",
  version_is_released: "Version",
  release_status: "Version",
  approval_status: "Version",
  reproducibility: "Version",
  release_date: "Version",
  latest_released: "Version",
  file_name: "Component",
  purl: "Component",
  file_path: "Component",
  publisher: "Component",
  license: "Component",
  tags: "Component",
  assessment_category: "Report",
  level_blocker: "Report",
  policy_category: "Issues",
  issue_status: "Issues",
  grade: "Issues",
  policy_id: "Issues",
  cve: "Vulnerabilities",
  marketing_name: "Vulnerabilities",
  cvss_severity: "Vulnerabilities",
  exploit: "Vulnerabilities",
  cwe_identifier: "Vulnerabilities",
  classification: "Malware",
  threat_platform: "Malware",
  threat_type: "Malware",
  threat_name: "Malware",
  service: "Secrets",
  endpoint: "Secrets",
  liveness: "Secrets",
  spdx_id: "Licenses",
  license_family: "Licenses",
};

export const isFilterDisabled = (
  name: keyof InsightsFilters,
  filters: InsightsFilters,
): boolean => {
  const mutuallyExclusiveFilters: Record<
    InsightsFilterCategories,
    Array<keyof InsightsFilters>
  > = {
    vulnerability: [
      "cve",
      "marketing_name",
      "cvss_severity",
      "exploit",
      "cwe_identifier",
    ],
    malware: [
      "classification",
      "threat_platform",
      "threat_type",
      "threat_name",
    ],
    secret: ["service", "endpoint", "liveness"],
    license: ["spdx_id", "license_family"],
  };
  const hasFiltersIn: Record<string, boolean> = {};

  [...Object.keys(filters), name].forEach((filter: keyof InsightsFilters) => {
    Object.keys(mutuallyExclusiveFilters).forEach(
      (filterGroup: InsightsFilterCategories) => {
        hasFiltersIn[filterGroup] =
          hasFiltersIn[filterGroup] ||
          mutuallyExclusiveFilters[filterGroup].includes(filter);
      },
    );
  });

  return Object.values(hasFiltersIn).filter((item) => item).length > 1;
};

export const getTotalReleaseStatusCount = (counts: Record<string, number>) =>
  Object.values(counts).reduce((prev, acc) => prev + acc, 0);

export const RISK_STATUS_TO_WEIGHT: Record<
  SDLCMetadata.assessment_status_t | "unknown",
  number
> = {
  fail: 3,
  warning: 2,
  pass: 1,
  unknown: 0,
};

export const getMostSignificantEvaluation = (
  evaluations: Insight["risk_assessment_evaluation"],
): Insight["risk_assessment_evaluation"][number] | undefined => {
  const prioritizedEvaluations = evaluations.toSorted((first, second) => {
    if (first.final || second.final) {
      return compareValues(first.final, second.final, true);
    }

    if (first.status !== second.status) {
      return compareValues(
        RISK_STATUS_TO_WEIGHT[first.status],
        RISK_STATUS_TO_WEIGHT[second.status],
        true,
      );
    }

    if (first.priority !== second.priority) {
      return compareValues(first.priority, second.priority, false);
    }

    return compareValues(first.count, second.count, true);
  });

  return prioritizedEvaluations[0];
};

export const getCategorizedSearches = (
  quickSearches: QuickInsightsSearch[] | undefined,
): Record<QuickSearchCategory, QuickInsightsSearch[]> => {
  const categorizedSearches: Record<
    QuickSearchCategory,
    QuickInsightsSearch[]
  > = {
    // order must be preserved!
    malware: [],
    tampering: [],
    secrets: [],
    vulnerabilities: [],
    hunting: [],
    licenses: [],
  };

  quickSearches?.forEach((search) => {
    if (search.category) categorizedSearches[search.category].push(search);
  });

  Object.keys(categorizedSearches).forEach((category: QuickSearchCategory) => {
    categorizedSearches[category].sort((first, second) =>
      compareValues(first.id, second.id, false),
    );
  });

  return categorizedSearches;
};

export type InsightsIndexTarget =
  | { type: "target"; to: string }
  | { type: "stale" }
  | { type: "none" };

export const resolveInsightsIndexSearch = (
  storedSearchId: string | null,
  quickSearches: QuickInsightsSearch[],
  mySearches: MyInsightsSearch[],
  groupId: string | undefined,
): InsightsIndexTarget => {
  if (storedSearchId) {
    const matchingQuickSearch = quickSearches.find(
      (search) => search.id === storedSearchId,
    );
    const matchingMySearch = mySearches.find(
      (search) =>
        search.id === storedSearchId &&
        (!search.group || search.group === groupId),
    );

    if (matchingMySearch || matchingQuickSearch) {
      const searchGroupId = matchingMySearch?.group ?? groupId;

      return {
        type: "target",
        to: `/${INSIGHTS_ROUTE}/group/${searchGroupId}/${INSIGHTS_SEARCH_ROUTE}/${storedSearchId}`,
      };
    }

    return { type: "stale" };
  }

  if (quickSearches.length > 0) {
    const categorizedSearches = getCategorizedSearches(quickSearches);

    return {
      type: "target",
      to: `${INSIGHTS_SEARCH_ROUTE}/${categorizedSearches.malware[0]?.id ?? quickSearches[0].id}`,
    };
  }

  return { type: "none" };
};
